/* @flow */
import Vue from 'vue'
import axios from 'axios'
import normalize from 'jsonapi-normalizer'
import oPath from 'object-path'
import query from 'query-string'

import {REMOVE_SESSION} from './session'
import {NEW_NOTIFICATION, REPLACE_NOTIFICATION} from './notifications'
// import type {JsonApiEntity} from '@/store'

export const REPLACE_POLICIES = 'REPLACE_POLICIES'
export const ADD_POLICIES = 'ADD_POLICIES'
export const MODIFY_POLICY = 'MODIFY_POLICY'
export const REMOVE_POLICY = 'REMOVE_POLICY'
export const START_POLICY_FETCHING = 'START_POLICY_FETCHING'
export const DONE_POLICY_FETCHING = 'DONE_POLICY_FETCHING'
export const SET_POLICIES_TOTAL = 'SET_POLICIES_TOTAL'

export const FETCH_POLICIES = 'FETCH_POLICIES'
export const FETCH_POLICY = 'FETCH_POLICY'
export const CREATE_POLICY = 'CREATE_POLICY'
export const UPDATE_POLICY = 'UPDATE_POLICY'
export const DELETE_POLICY = 'DELETE_POLICY'
export const RESET_STATE = 'RESET_STATE'
export const PUSH_EUI = 'PUSH_EUI'
export const REMOVE_EUI = 'REMOVE_EUI'
export const MODIFY_EUI = 'MODIFY_EUI'

const initialState = () => ({
  entities: {},
  statistics: {
    total: null,
  },
  fetch_status: {
    all: '',
  },
})

export default {
  state: initialState(),
  getters: {
    policyDefaults: () => ({end_device_euis_from_eui_filters: []}),
    policies: ({entities = {}}: Object) =>
      //$FlowFixMe
      Object.keys(entities).map(key => entities[key]),
    policiesKeyedByEui: (state: Object, getters: Object) => {
      const policies = {}
      getters.policies.forEach(policy => {
        policies[policy.eui] = policy
      })
      return policies
    },
    policyById: (state: Object) => (id: string) => state.entities[id] || {},
    policyByEui: (state: Object, getters: Object) => (eui: string) =>
      getters.policies.find(policy => policy.eui === eui) || {},
  },
  mutations: {
    [PUSH_EUI](state: Object, payload: Object) {
      state.entities[payload.id].end_device_euis_from_eui_filters.push(payload.entity)
    },
    [REMOVE_EUI](state: Object, payload: Object) {
      state.entities[payload.id].end_device_euis_from_eui_filters.splice(payload.index, 1)
    },
    [MODIFY_EUI](state: Object, payload: Object) {
      state.entities[payload.id].end_device_euis_from_eui_filters[payload.index].min_eui =
        payload.entity
    },
    [RESET_STATE](state: Object) {
      Object.assign(state, initialState())
      state.entities = initialState().entities
    },
    [REMOVE_SESSION](state: Object) {
      state.entities = initialState().entities
    },
    [START_POLICY_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loading'
    },
    [DONE_POLICY_FETCHING](state: Object, payload: Object) {
      state.fetch_status[payload.key] = 'loaded'
    },
    [REPLACE_POLICIES](state: Object, {entities = {}}: Object) {
      state.entities = entities
    },
    [ADD_POLICIES](state: Object, payload: Object) {
      state.entities = {
        ...state.entities,
        ...payload.entities,
      }
    },
    [MODIFY_POLICY](state: Object, payload: Object) {
      state.entities[payload.id] = {
        ...state.entities[payload.id],
        ...payload,
      }
    },
    [REMOVE_POLICY](state: Object, payload: Object) {
      Vue.delete(state.entities, payload.id)
    },
    [SET_POLICIES_TOTAL](state: Object, payload: Object) {
      state.statistics.total = payload.total
    },
  },
  actions: {
    [RESET_STATE]: async function(state: Object) {
      this.commit(RESET_STATE, state)
    },
    [FETCH_POLICIES]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        commit(START_POLICY_FETCHING, {key: 'all'})
        let pathstring = '/policies?limit=200'

        if (!!payload && payload.orgId) {
          pathstring = '/organizations/' + payload.orgId + '/policies'
        }

        const {data} = await axios.get(pathstring)

        commit(SET_POLICIES_TOTAL, {
          total: data.meta.pagination.collection_count,
        })
        const entities = {}
        data.data.map(policy => {
          entities[policy.id] = {
            ...policy.attributes,
            ...policy.meta,
          }
        })

        commit(REPLACE_POLICIES, {
          entities,
        })
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to load policies.',
          detail: err.message,
          color: 'error',
        })
      } finally {
        commit(DONE_POLICY_FETCHING, {key: 'all'})
      }
    },
    [FETCH_POLICY]: async function({commit, dispatch}: Object, payload: Object) {
      try {
        const {
          data: {data},
        } = await axios.get(`/policies/${payload.id}`)

        // gateways_from
        if (!data.relationships['gateways_from_gateways'])
          data.relationships['gateways_from_gateways'] = []
        data.relationships.gateways_from.data.gateways.forEach(elem =>
          data.relationships['gateways_from_gateways'].push({
            type: 'gateway',
            id: elem['id'],
          })
        )

        if (!data.relationships['gateways_from_application_networks'])
          data.relationships['gateways_from_application_networks'] = []
        data.relationships.gateways_from.data.application_networks.forEach(elem =>
          data.relationships['gateways_from_application_networks'].push({
            type: 'application_network',
            id: elem['id'],
          })
        )

        delete data.relationships.gateways_from

        //end_device_euis_from_application_networks
        if (!data.relationships['end_device_euis_from_application_networks'])
          data.relationships['end_device_euis_from_application_networks'] = []
        data.relationships.end_device_euis_from.data.application_networks.forEach(elem =>
          data.relationships['end_device_euis_from_application_networks'].push({
            type: 'application_network',
            id: elem['id'],
          })
        )

        if (!data.relationships['end_device_euis_from_device_groups'])
          data.relationships['end_device_euis_from_device_groups'] = []
        data.relationships['end_device_euis_from_device_groups'] =
          data.relationships.end_device_euis_from.data.device_groups

        if (!data.relationships['end_device_euis_from_eui_filters'])
          data.relationships['end_device_euis_from_eui_filters'] = []
        data.relationships.end_device_euis_from_eui_filters =
          data.relationships.end_device_euis_from.data.eui_filters
        data.relationships.end_device_euis_from_eui_filters.forEach(elem => {
          if (!(elem.max_eui === elem.min_eui)) {
            elem.filter_type = elem.filter_type.concat('-range')
          }
        })
        delete data.relationships.end_device_euis_from

        commit(ADD_POLICIES, {
          entities: {
            [data.id]: {
              ...data.attributes,
              ...data.relationships,
              ...data.meta,
            },
          },
        })
        return {success: true}
      } catch (err) {
        const status = oPath.get(err, 'response.status')
        if (status !== 404) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Failed to load policy.',
            detail: err.message,
            color: 'error',
          })
        }
        return {success: false, status}
      }
    },
    [CREATE_POLICY]: async function({commit, dispatch}: Object, payload: Object) {
      // massage data to make api happy
      let relationships = {}
      // handle the "Applies To" section:
      let gatewaysFrom = {
        application_network_ids: payload.relationships.gateways_from_application_networks
          ? payload.relationships.gateways_from_application_networks.data
          : [],
        gateway_ids: payload.relationships.gateways_from_gateways
          ? payload.relationships.gateways_from_gateways.data
          : [],
      }
      gatewaysFrom.gateway_ids = gatewaysFrom.gateway_ids
        .filter(i => i && i['id'])
        .map(i => i['id'])
      gatewaysFrom.application_network_ids = gatewaysFrom.application_network_ids
        .filter(i => i && i['id'])
        .map(i => i['id'])
      relationships.gateways_from = gatewaysFrom
      delete payload.relationships['gateways_from_application_networks']
      delete payload.relationships['gateways_from_gateways']

      // Handle the "Filters" section here:

      // Handle dev euis
      if (!relationships.end_device_euis_from) relationships.end_device_euis_from = {}
      if (!relationships.end_device_euis_from.eui_filters)
        relationships.end_device_euis_from.eui_filters =
          payload.relationships.end_device_euis_from_eui_filters.data

      if (!relationships.end_device_euis_from.application_networks)
        relationships.end_device_euis_from.application_networks = []

      // handle application networks
      if (payload.relationships['end_device_euis_from_application_networks']) {
        payload.relationships.end_device_euis_from_application_networks.data.forEach(
          elem =>
            relationships.end_device_euis_from['application_networks'].push({
              id: elem['id'],
            })
        )
        delete payload.relationships['end_device_euis_from_application_networks']
      }
      // handle device groups
      if (!relationships.end_device_euis_from.device_groups)
        relationships.end_device_euis_from.device_groups = []
      if (payload.relationships['end_device_euis_from_device_groups']) {
        payload.relationships['end_device_euis_from_device_groups'].data.forEach(elem =>
          relationships.end_device_euis_from['device_groups'].push({
            id: elem,
          })
        )

        delete payload.relationships['end_device_euis_from_device_groups']
      }

      payload.relationships = relationships

      try {
        const {data} = await axios.post('/policies', {
          data: {
            type: 'policies',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(ADD_POLICIES, {entities: entities.policies})
        if (!payload.suppressNotifications) {
          dispatch(NEW_NOTIFICATION, {
            text: 'Saved new policy!',
            color: 'success',
          })
        }
        return data.data.attributes.id
      } catch (err) {
        dispatch(NEW_NOTIFICATION, {
          text: 'Failed to save policy',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [UPDATE_POLICY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: 'Saving...',
          undoable: true,
          color: 'secondary',
        })
      }
      // massage data to make api happy
      let relationships = {}
      // handle the "Applies To" section:
      let gatewaysFrom = {
        application_network_ids: payload.relationships[
          'gateways_from_application_networks'
        ]
          ? payload.relationships['gateways_from_application_networks'].data
          : [],
        gateway_ids: payload.relationships['gateways_from_gateways']
          ? payload.relationships['gateways_from_gateways'].data
          : [],
      }
      gatewaysFrom.gateway_ids = gatewaysFrom.gateway_ids
        .filter(i => i && i['id'])
        .map(i => i['id'])
      gatewaysFrom.application_network_ids = gatewaysFrom.application_network_ids
        .filter(i => i && i['id'])
        .map(i => i['id'])
      relationships.gateways_from = gatewaysFrom
      delete payload.relationships['gateways_from_application_networks']
      delete payload.relationships['gateways_from_gateways']

      // Handle the "Filters" section here:

      // Handle dev euis
      if (!relationships.end_device_euis_from) relationships.end_device_euis_from = {}
      if (!relationships.end_device_euis_from.eui_filters)
        relationships.end_device_euis_from.eui_filters =
          payload.relationships.end_device_euis_from_eui_filters.data

      if (!relationships.end_device_euis_from.application_networks)
        relationships.end_device_euis_from.application_networks = []

      // handle application networks
      if (payload.relationships.end_device_euis_from_application_networks) {
        payload.relationships.end_device_euis_from_application_networks.data.forEach(
          elem =>
            relationships.end_device_euis_from['application_networks'].push({
              id: elem['id'],
            })
        )
        delete payload.relationships['end_device_euis_from_application_networks']
      }
      // handle device groups
      if (!relationships.end_device_euis_from.device_groups)
        relationships.end_device_euis_from.device_groups = []
      if (
        payload.relationships.end_device_euis_from_device_groups &&
        payload.relationships.end_device_euis_from_device_groups.data
      ) {
        /*eslint-disable */
        payload.relationships.end_device_euis_from_device_groups.data.forEach(elem =>{
          // console.log(elem)
          // # this bug fix is bit unclean.. the elem should always be a string, but what happens
          // is that when the user chooses items in the selector, elem is a string with the id 
          // of the device_group. But if they just click update on a form where they had not
          // updated this input selector, elem is for some reason an object 
          if(typeof elem === "string"){
            return relationships.end_device_euis_from.device_groups.push({
              id: elem,
            })
          }else{
            return relationships.end_device_euis_from.device_groups.push({
              id: elem["id"],
            })
          }
        }

        )
        /*eslint-enable */
        delete payload.relationships['end_device_euis_from_device_groups']
      }

      payload.relationships = relationships
      // xhr call
      try {
        const {data} = await axios.patch(`/policies/${payload.id}`, {
          data: {
            type: 'policies',
            attributes: payload.attributes,
            relationships: payload.relationships,
          },
        })
        const {entities} = normalize(data)
        commit(MODIFY_POLICY, entities.policies[payload.id])
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: 'Saved!',
            color: 'success',
            undoable: true,
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to update policy ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
    [DELETE_POLICY]: async function({commit, dispatch}: Object, payload: Object) {
      let notificationId
      if (!payload.suppressNotifications) {
        notificationId = await dispatch(NEW_NOTIFICATION, {
          text: `Deleting policy...`,
          color: 'secondary',
        })
      }

      try {
        await axios.delete(`/policies/${payload.id}?${query.stringify(payload.options)}`)
        commit(REMOVE_POLICY, payload)
        if (!payload.suppressNotifications) {
          commit(REPLACE_NOTIFICATION, {
            id: notificationId,
            text: `Deleted policy!`,
            color: 'success',
          })
        }
        return true
      } catch (err) {
        commit(REPLACE_NOTIFICATION, {
          id: notificationId,
          text: 'Failed to delete policy ',
          detail: err.message,
          color: 'error',
        })
      }
      return false
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/policies.js